import { useQuery } from "@tanstack/react-query";

import "./reportingModuleToggleHandlers";
import { reportingQueryKeys } from "./query-keys";
import {
  buildReportingVacancyParams,
  tenantClientParams,
} from "./reportingQueryUtils";
import type {
  ReportingLevel,
  GeneralReportingDataParams,
  MetricsBySegment,
  ModuleIntegrationsResponse,
  PaginatedResponse,
  RecordBySegment,
  ReportingDataParams,
  ReportingFilterCountsResponse,
} from "./types";

import { instance } from "@/lib/axios/instance";
import { formatLocalDateOnly } from "@/utils/dateUtils";

type ReportingEndpoint = "aggregated" | "aggregatedMetrics" | "filterCounts";

type EndpointResult<
  E extends ReportingEndpoint,
  S extends ReportingLevel,
> = E extends "aggregated"
  ? PaginatedResponse<RecordBySegment[S]>
  : E extends "aggregatedMetrics"
    ? MetricsBySegment[S]
    : ReportingFilterCountsResponse;

type ReportingDataOptions<
  E extends ReportingEndpoint,
  S extends ReportingLevel,
> = {
  endpoint: E;
  level: S;
  params: GeneralReportingDataParams;
  enabled?: boolean;
};

function buildRequestParams(params: GeneralReportingDataParams) {
  const vacancyParams = buildReportingVacancyParams(params.vacancyFilters);

  return {
    startDate: params.startDate
      ? formatLocalDateOnly(params.startDate)
      : undefined,
    endDate: params.endDate ? formatLocalDateOnly(params.endDate) : undefined,
    ...tenantClientParams(params.tenantId, params.clientId),
    title: params.searchValue,
    page: params.page,
    pageSize: params.pageSize,
    sortColumn: params.sortColumn,
    sortDirection: params.sortDirection,
    integrations: params.integrations?.length ? params.integrations : undefined,
    campaignIds: params.campaignIds?.length ? params.campaignIds : undefined,
    ...vacancyParams,
  };
}

function buildKeyParams(params: GeneralReportingDataParams) {
  return {
    ...params,
    startDate: params.startDate
      ? formatLocalDateOnly(params.startDate)
      : undefined,
    endDate: params.endDate ? formatLocalDateOnly(params.endDate) : undefined,
  };
}

function getEndpointUrl(endpoint: ReportingEndpoint, level: ReportingLevel): string {
  switch (endpoint) {
  case "aggregated":
    return `/api/v2/Reporting/${level}/Aggregated`;
  case "aggregatedMetrics":
    return `/api/v2/Reporting/${level}/Metrics/Aggregated`;
  case "filterCounts":
    return `/api/v2/Reporting/${level}/FilterCounts`;
  }
}

function getEndpointQueryKey(
  endpoint: ReportingEndpoint,
  level: ReportingLevel,
  keyParams: Record<string, unknown>,
) {
  switch (endpoint) {
  case "aggregated":
    return reportingQueryKeys.aggregated(level, keyParams);
  case "aggregatedMetrics":
    return reportingQueryKeys.aggregatedMetrics(level, keyParams);
  case "filterCounts":
    return reportingQueryKeys.filterCounts(level, keyParams);
  }
}

export function useReportingData<
  E extends ReportingEndpoint,
  S extends ReportingLevel,
>(options: ReportingDataOptions<E, S>) {
  const { endpoint, level, params, enabled } = options;
  const keyParams = buildKeyParams(params);

  return useQuery<EndpointResult<E, S>>({
    queryKey: getEndpointQueryKey(endpoint, level, keyParams),
    queryFn: () =>
      instance
        .get<EndpointResult<E, S>>(getEndpointUrl(endpoint, level), {
          params: buildRequestParams(params),
        })
        .then((res) => res.data),
    enabled,
  });
}

export function useIntegrationReportingAggregated<T>(
  integrationName: string,
  params: ReportingDataParams,
) {
  const keyParams = buildKeyParams(params);

  return useQuery({
    queryKey: reportingQueryKeys.integrationAggregated(
      integrationName,
      keyParams,
    ),
    queryFn: () =>
      instance
        .get<PaginatedResponse<T>>(
          `/api/v2/Integrations/${encodeURIComponent(integrationName)}/Reporting/aggregated`,
          { params: buildRequestParams(params) },
        )
        .then((res) => res.data),
    enabled: !!(params.tenantId || params.clientId),
  });
}

export function useReportingIntegrations(
  params: {
    tenantId?: string | null;
    clientId?: string | null;
    isActive?: boolean | null;
  },
  options?: { enabled?: boolean },
) {
  const { tenantId, clientId, isActive } = params;
  return useQuery({
    queryKey: reportingQueryKeys.integrations(
      tenantId ?? undefined,
      clientId ?? undefined,
      isActive ?? undefined,
    ),
    enabled: options?.enabled ?? true,
    queryFn: () =>
      instance
        .get<ModuleIntegrationsResponse>(
          "/api/v2/Modules/reporting/integrations",
          {
            params: {
              ...tenantClientParams(
                tenantId ?? undefined,
                clientId ?? undefined,
              ),
              isActive: isActive ?? undefined,
            },
          },
        )
        .then((res) => res.data),
  });
}
